import React, { useId, useState } from 'react';
import { PASSWORD_MAX_LENGTH, PASSWORD_REQUIREMENTS } from '@shared/schemas/password.schema.js';
import { Button } from '@/components/common';
import { AvatarSelector } from '@/components/common/AvatarSelector';
import { 
  FaUser, 
  FaEnvelope, 
  FaLock, 
  FaLockOpen, 
  FaUserPlus, 
  FaExclamationTriangle
} from 'react-icons/fa';
import { PasswordRequirementsChecklist } from './PasswordRequirementsChecklist';

/** What the invitee chooses. The email is not among them: see `lockedEmail`. */
export interface RegisterFormValues {
  username: string;
  password: string;
  avatar: string;
}

interface RegisterFormProps {
  onSubmit: (data: RegisterFormValues) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

interface RegisterFormData {
  username: string;
  password: string;
  confirmPassword: string;
  avatar: string;
}

interface RegisterFieldErrors {
  username?: string;
  password?: string;
  confirmPassword?: string;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({ onSubmit, isLoading, error }) => {
  const [formData, setFormData] = useState<RegisterFormData>({
    username: '',
    password: '',
    confirmPassword: '',
    avatar: 'premade_01'
  });
  const [fieldErrors, setFieldErrors] = useState<RegisterFieldErrors>({});
  // Generated ids keep label/input pairs unique if the form is ever mounted twice.
  const idPrefix = useId();
  const fieldId = (name: keyof RegisterFormData) => `${idPrefix}-${name}`;
  const passwordRequirementsId = `${idPrefix}-password-requirements`;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear field error when user starts typing
    if (fieldErrors[name as keyof RegisterFieldErrors]) {
      setFieldErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateUsername = (username: string): string | undefined => {
    if (!username) return 'Username is required';
    if (username.length < 3) return 'Username must be at least 3 characters';
    if (username.length > 50) return 'Username must be at most 50 characters';
    if (!/^[a-zA-Z0-9_]+$/.test(username)) return 'Username can only contain letters, numbers, and underscores';
    return undefined;
  };

  const validatePassword = (password: string): string | undefined => {
    if (!password) return 'Password is required';
    if (PASSWORD_REQUIREMENTS.some(requirement => !requirement.test(password))) {
      return 'Password does not meet all the requirements below';
    }
    return undefined;
  };

  const validateConfirmPassword = (confirmPassword: string, password: string): string | undefined => {
    if (!confirmPassword) return 'Please confirm your password';
    if (password !== confirmPassword) return 'Passwords do not match';
    return undefined;
  };

  const validateForm = (): boolean => {
    const errors: RegisterFieldErrors = {};
    
    errors.username = validateUsername(formData.username);
    errors.password = validatePassword(formData.password);
    errors.confirmPassword = validateConfirmPassword(formData.confirmPassword, formData.password);
    
    setFieldErrors(errors);
    return !Object.values(errors).some(error => error);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || isLoading) return;
    
    const { username, password, avatar } = formData;
    await onSubmit({ username, password, avatar });
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    let error: string | undefined;
    if (name === 'username') {
      error = validateUsername(value);
    } else if (name === 'password') {
      error = validatePassword(value);
    } else if (name === 'confirmPassword') {
      error = validateConfirmPassword(value, formData.password);
    }
    
    setFieldErrors(prev => ({ ...prev, [name]: error }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Error Message */}
      {error && (
        <div className="p-4 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-lg">
          <div className="flex items-center gap-3">
            <FaExclamationTriangle className="text-game-golden flex-shrink-0" />
            <span className="font-medium">{error}</span>
          </div>
        </div>
      )}
      
      {/* No email field, and no address shown either. The preview endpoint is
          public, so naming the invited mailbox would tell whoever holds a
          forwarded link the one thing they do not already know (§6.1). The
          server reads it from the invitation. */}
      <div className="flex items-start gap-3 p-3 bg-game-medium/20 border border-game-medium/50 rounded-lg text-sm text-gray-200">
        <FaEnvelope className="text-game-golden flex-shrink-0 mt-0.5" />
        <span>Your account will use the email address this invitation was sent to.</span>
      </div>

      {/* Username Field */}
      <div>
        <label htmlFor={fieldId('username')} className="block text-sm font-semibold text-white mb-2">
          <div className="flex items-center gap-2">
            <FaUser className="text-game-golden" />
            Username
          </div>
        </label>
        <input
          type="text"
          id={fieldId('username')}
          name="username"
          value={formData.username}
          onChange={handleInputChange}
          onBlur={handleBlur}
          required
          autoComplete="username"
          minLength={3}
          maxLength={50}
          pattern="^[a-zA-Z0-9_]+$"
          title="Username can only contain letters, numbers, and underscores (3-50 characters)"
          className={`w-full px-4 py-3 bg-game-cream border border-game-medium/50 rounded-lg text-gray-900 placeholder-gray-600 focus:border-game-golden focus:bg-game-cream/90 focus:shadow-lg focus:shadow-game-golden/20 focus:outline-none transition-all duration-200 ${
            fieldErrors.username ? 'border-game-orange bg-game-orange/20' : ''
          }`}
          placeholder="Choose a username"
        />
        {fieldErrors.username && (
          <div className="mt-2 p-2 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-md text-sm flex items-center gap-2">
            <FaExclamationTriangle className="text-game-golden flex-shrink-0" />
            {fieldErrors.username}
          </div>
        )}
      </div>

      {/* Password Field */}
      <div>
        <label htmlFor={fieldId('password')} className="block text-sm font-semibold text-white mb-2">
          <div className="flex items-center gap-2">
            <FaLock className="text-game-golden" />
            Password
          </div>
        </label>
        <input
          type="password"
          id={fieldId('password')}
          name="password"
          value={formData.password}
          onChange={handleInputChange}
          onBlur={handleBlur}
          required
          autoComplete="new-password"
          maxLength={PASSWORD_MAX_LENGTH}
          aria-describedby={passwordRequirementsId}
          className={`w-full px-4 py-3 bg-game-cream border border-game-medium/50 rounded-lg text-gray-900 placeholder-gray-600 focus:border-game-golden focus:bg-game-cream/90 focus:shadow-lg focus:shadow-game-golden/20 focus:outline-none transition-all duration-200 ${
            fieldErrors.password ? 'border-game-orange bg-game-orange/20' : ''
          }`}
          placeholder="Create a strong password"
        />
        {fieldErrors.password && (
          <div className="mt-2 p-2 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-md text-sm flex items-center gap-2">
            <FaExclamationTriangle className="text-game-golden flex-shrink-0" />
            {fieldErrors.password}
          </div>
        )}
        <PasswordRequirementsChecklist id={passwordRequirementsId} password={formData.password} />
      </div>

      {/* Confirm Password Field */}
      <div>
        <label htmlFor={fieldId('confirmPassword')} className="block text-sm font-semibold text-white mb-2">
          <div className="flex items-center gap-2">
            <FaLockOpen className="text-game-accent" />
            Confirm Password
          </div>
        </label>
        <input
          type="password"
          id={fieldId('confirmPassword')}
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleInputChange}
          onBlur={handleBlur}
          required
          autoComplete="new-password"
          maxLength={PASSWORD_MAX_LENGTH}
          className={`w-full px-4 py-3 bg-game-cream border border-game-medium/50 rounded-lg text-gray-900 placeholder-gray-600 focus:border-game-golden focus:bg-game-cream/90 focus:shadow-lg focus:shadow-game-golden/20 focus:outline-none transition-all duration-200 ${
            fieldErrors.confirmPassword ? 'border-game-orange bg-game-orange/20' : ''
          }`}
          placeholder="Confirm your password"
        />
        {fieldErrors.confirmPassword && (
          <div className="mt-2 p-2 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-md text-sm flex items-center gap-2">
            <FaExclamationTriangle className="text-game-golden flex-shrink-0" />
            {fieldErrors.confirmPassword}
          </div>
        )}
      </div>

      {/* Avatar */}
      <div>
        <span className="block text-sm font-semibold text-white mb-2">
          <span className="flex items-center gap-2">
            <FaUser className="text-game-golden" />
            Choose your character
          </span>
        </span>
        <AvatarSelector
          selectedAvatar={formData.avatar}
          onAvatarSelect={(avatar) => setFormData(prev => ({ ...prev, avatar }))}
          disabled={isLoading}
        />
      </div>

      {/* Submit Button */}
      <Button 
        type="submit" 
        variant="primary"
        isLoading={isLoading}
        className="w-full px-6 py-3 mt-6"
      >
        <FaUserPlus className="text-lg" />
        <span>Create Account</span>
      </Button>
    </form>
  );
};